import { useState, useEffect } from 'react';
import { ENV_CONFIG } from '../config/environment';

/**
 * Custom hook for debouncing values
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Custom hook for debouncing search input
 * @param searchTerm - The search term to debounce
 * @param delay - The delay in milliseconds (uses environment config if not provided)
 * @returns The debounced search term
 */
export function useDebouncedSearch(searchTerm: string, delay?: number): string {
  return useDebounce(searchTerm, delay ?? ENV_CONFIG.DEBOUNCE_DELAY);
}
